import { useAuth } from '@agentnet/auth';
import { useIsFeatureEnabled } from '@agentnet/feature-flags';
import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Avatar, Grid, IconButton, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Edit } from '@material-ui/icons';
import { SaveStaffProfileDetails, SaveUserProfileDetails } from 'api/admin/interfaces';
import {
  AddUserAcessKeys,
  getProfileDetails,
  getUserAcessKeys,
  getVendorOptions,
  setUserProfileDetails,
  updateUserProfileContact,
} from 'api/admin/user-profile-api';
import { clearUserCache } from 'api/file/file-api';
import { BopsPreferences, userKeyPref, userProfile } from 'api/profile/types';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import useAsync from 'hooks/useAsync';
import { useUnsavedNavigationGuard } from 'hooks/useUnsavedNavigationGuard';
import _ from 'lodash';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import BottomAppBar from 'ui-kit/components/bottomAppBar/BottomAppBar';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import UnsavedChangesDialog from 'ui-kit/components/modal/UnsavedChangesDialog';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import SearchOrderEmailsTable, {
  SearchOrderEmailsTableRef,
} from 'ui-kit/components/tables/AdminUserSearchOrderEmailsTable';
import { AgentNetTextInput } from 'ui-kit/inputs';
import AgentNetDropdownSelector from 'ui-kit/inputs/AgentNetDropdownSelector';
import PhoneField from 'ui-kit/inputs/PhoneField/PhoneField';
import BopsPreferencesChecklist from './BopsPreferencesChecklist';
import { FeatureFlag } from 'utilities/constants/launch-darkly-flags';
import {
  PROFILE_SAVE_OK_STATUS,
  PROFILE_SAVE_SECTION_LABELS,
  PROFILE_SAVE_UNSET_STATUS,
  UserRoles,
  UserRoleTypes,
} from './constants';
import { getFilteredSortedBopsOptions, UserOffices } from './interfaces';
import UserAccessKeyComponent, {
  AccessKeyOptionType,
  UserAccessKeyComponentRef,
  UserAccessKeyType,
} from './UserAccessKey';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      background: theme.palette.common.white,
    },
    contentWrap: {
      padding: pxToRem(0, 0, 32),
    },
    mb2: {
      marginBottom: theme.spacing(2),
    },
    userBannerWrap: {
      padding: theme.spacing(3),
      borderBottom: `1px solid #c4c4c4`,
    },
    userBannerContainer: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    userBannerDetails: { display: 'flex', alignItems: 'center' },
    userDetails: {
      marginLeft: theme.spacing(2),
    },
    userName: {
      marginBottom: theme.spacing(0.5),
    },
    avatar: {
      width: '64px',
      height: '64px',
      border: `1px solid ${theme.palette.primary.dark}`,
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
    captionDivider: {
      width: theme.spacing(2),
      display: 'inline-block',
    },
    fieldSection: {
      padding: theme.spacing(2, 3),
    },
    fieldSectionLeft: {
      marginBottom: theme.spacing(2),
    },
    sectionHeaderRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(2),
    },
    fieldSectionRight: {
      marginTop: 0,
    },
    preferenceContent: {
      width: '100%',
      boxSizing: 'border-box',
      padding: theme.spacing(2, 3),
    },
    adornmentIcon: {
      padding: 0,
      '&:hover': {
        background: 'transparent',
      },
    },
    ctaBtn: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      marginBottom: theme.spacing(3),
      width: '100%',
    },
    alertContainer: {
      marginBottom: '63px',
    },
    alertMsg: {
      width: '-webkit-fill-available',
      position: 'absolute',
      bottom: '63px',
      left: 0,
      padding: '0 35px',
    },
  }),
);

interface AdminProfileBannerProps {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  userId?: number | string;
  payConnectionId?: string;
  roleId?: any;
  onChangeClick: () => void;
}

const AdminUserProfileBanner = ({
  firstName,
  middleName,
  lastName,
  userId,
  roleId,
  onChangeClick,
}: AdminProfileBannerProps) => {
  const classes = useStyles();
  function nameTag() {
    const fname = firstName ? firstName[0].toUpperCase() : '';
    const lname = lastName ? lastName[0].toUpperCase() : '';
    return `${fname}${lname}`;
  }
  return (
    <div className={classes.userBannerWrap}>
      <div className={classes.userBannerContainer}>
        <div className={classes.userBannerDetails}>
          <Avatar className={classes.avatar} data-testid="UserProfileAvatar">
            {firstName && lastName ? nameTag() : 'JT'}
          </Avatar>
          <div className={classes.userDetails}>
            <Typography variant="h1" className={classes.userName}>
              {((firstName && firstName.trim()) || '') +
                (firstName && (middleName || lastName) ? ' ' : '') +
                ((middleName && middleName.trim()) || '') +
                (middleName && lastName ? ' ' : '') +
                (lastName && lastName.trim()) || ''}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {userId && `User ID: ${userId} `}
              <span className={classes.captionDivider}></span>
              {/* {payConnectionId && `Pay Connection ID: ${payConnectionId} `} */}
            </Typography>
          </div>
        </div>
        {roleId === UserRoleTypes.Agent ? (
          <div className="userBannerPw">
            <AgentNetButton
              variant="contained"
              color="primary"
              size="small"
              data-testid="UserProfileChangePassword"
              onClick={onChangeClick}
            >
              Change Password
            </AgentNetButton>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const AdminUserProfileContent = () => {
  const { getAccessToken } = useAuth();
  const idaaSAuth = useAuth();
  const classes = useStyles();
  const isCoverSheetEnabled = useIsFeatureEnabled(FeatureFlag.SHOW_COVER_SHEET_OPTION);
  const [resData, setData] = useState<userProfile>();
  const [userAccessKeyData, setuserAccessKeyData] = useState<UserAccessKeyType[]>();
  const [defaultOfficeLocation, setdefaultOfficeLocation] = useState('');
  const officeDetails: Partial<UserOffices> = {};
  const [currentOfficeDetail, setCurrentOfficeDetail] = useState(officeDetails);
  const [updatedOfficeDetail, setUpdatedOffice] = useState(officeDetails);
  const [bopsPreferences, setBopsPreferences] = useState<BopsPreferences[]>([]);
  const [updatedServiceOrderEmailNotification, setupdatedServiceOrderEmailNotification] = useState<emailType[]>([]);
  const { addSnackbarMessage } = useSnackBars();
  const [isSaveDisabled, setisSaveDisabled] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const [contactPhoneNumber, setContactPhoneNumber] = useState('');
  const [phoneNumberError, setPhoneNumberError] = useState('');
  interface emailType {
    ServiceOrderEmailNotificationId: number;
    ServiceOrderEmailAddress: string;
  }
  const [initialBopsPreferences, setinitialBopsPreferences] = useState<BopsPreferences[]>([]);

  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const emailTableRef = useRef<SearchOrderEmailsTableRef>(null);
  const userAccessKeyRef = useRef<UserAccessKeyComponentRef>(null);

  const getProfileData = async () => {
    const token = await getAccessToken();
    const responsedata = await getProfileDetails(token);
    const userAccessresponse = await getUserAcessKeys(token);
    setuserAccessKeyData(userAccessresponse);
    responsedata?.FirmAccess[0]?.Offices?.map((o: any) => {
      if (o.OfficeId === responsedata.DefaultFirmLocationId) {
        o.Name = `${o.Name} (Default Office)`;
      }
    });
    setContactPhoneNumber(responsedata?.PhoneNumber ?? '');
    setupdatedServiceOrderEmailNotification(
      responsedata?.ServiceOrderEmailNotification?.map((email: emailType) => ({
        ServiceOrderEmailNotificationId: email.ServiceOrderEmailNotificationId,
        ServiceOrderEmailAddress: email.ServiceOrderEmailAddress,
      })) ?? [],
    );
    setData(responsedata);
  };

  const { execute: executegetProfileData, status: getProfileDataStatus } = useAsync(getProfileData, false);

  const actionStatus = getProfileDataStatus === 'pending' ? 'pending' : 'idle';

  useEffect(() => {
    if (localStorage.getItem('isProfileEdited') === 'true') {
      updateIdaasData();
    } else {
      executegetProfileData().then();
    }
  }, []);

  useEffect(() => {
    if (resData != undefined && resData?.PhoneNumber != undefined) {
      setContactPhoneNumber(resData?.PhoneNumber);
    }
  }, [resData]);

  const updateIdaasData = async () => {
    const token = await getAccessToken();
    const request: SaveStaffProfileDetails = {
      PhoneNumber: '',
    };
    const profileContactResponse = await updateUserProfileContact(token, request);
    if (profileContactResponse) {
      localStorage.setItem('isProfileEdited', 'false');
      window.location.reload();
    }
  };

  const saveUserProfileDetails = async (
    contactPhoneNumber: string,
    profileOfficeRequest: Partial<UserOffices>,
    ServiceOrderEmailRequest: emailType[],
    bopsUpdatedRequest: BopsPreferences[],
  ) => {
    const request: SaveUserProfileDetails = {
      StaffContactRequest: undefined,
      ProfileDefaultOfficeRequest: undefined,
      SOEmailRequest: undefined,
      BOPSPreferenceRequest: undefined,
    };

    if (contactPhoneNumber != undefined && contactPhoneNumber != '') {
      request.StaffContactRequest = {
        PhoneNumber: contactPhoneNumber,
      };
    }

    if (ServiceOrderEmailRequest[0]?.ServiceOrderEmailNotificationId != undefined) {
      request.SOEmailRequest = {
        searchOrderEmailDetails: ServiceOrderEmailRequest,
      };
    }

    const token = await getAccessToken();
    if (profileOfficeRequest.OfficeId != undefined && !_.isEqual(profileOfficeRequest, currentOfficeDetail)) {
      request.ProfileDefaultOfficeRequest = {
        DefaultFirmLocationId: profileOfficeRequest.OfficeId,
        PhoneNumber: profileOfficeRequest.PhoneNumber,
        FirmLicense: profileOfficeRequest.FirmLicense,
      };
    }
    if (bopsUpdatedRequest.length > 0 && !_.isEqual(bopsPreferences, initialBopsPreferences)) {
      const mappedBopsid = bopsUpdatedRequest.slice(1).map((o) => {
        return o.id;
      });
      request.BOPSPreferenceRequest = {
        BopsPreferenceId: mappedBopsid,
      };
    }
    const result = await setUserProfileDetails(token, request);

    // Parse the "<Key>: <status>" pairs into a map of trimmed statuses so the checks below don't
    // depend on the surrounding whitespace the endpoint returns.
    const statusByKey: Record<string, string> = {};
    if (result) {
      result.split('  ').forEach((pair) => {
        const [key, status] = pair.split(':');
        if (key) {
          statusByKey[key.trim()] = (status ?? '').trim();
        }
      });
    }

    const isSavedOrSkipped = (status?: string) =>
      status === PROFILE_SAVE_OK_STATUS || status === PROFILE_SAVE_UNSET_STATUS;
    const hasFailure = Object.values(statusByKey).some((status) => !isSavedOrSkipped(status));

    if (!hasFailure) {
      addSnackbarMessage({ message: `Saving  Successful`, type: 'success' });
    } else {
      Object.keys(statusByKey).forEach((key) => {
        const label = PROFILE_SAVE_SECTION_LABELS[key];
        if (!label) return;
        if (statusByKey[key] === PROFILE_SAVE_OK_STATUS) {
          addSnackbarMessage({ message: `${label} Saving  Successful`, type: 'success' });
        } else if (statusByKey[key] !== PROFILE_SAVE_UNSET_STATUS) {
          addSnackbarMessage({ message: `${label} Save Failed`, type: 'error' });
        }
      });
    }

    const officeSavedSuccessfully = statusByKey['OfficeResponse'] === PROFILE_SAVE_OK_STATUS;
    if (request.ProfileDefaultOfficeRequest != undefined && officeSavedSuccessfully) {
      try {
        if (profileCtx.profile?.login) {
          await clearUserCache(profileCtx.profile.login, token);
        }
        if (hasFailure) {
          await executegetProfileData();
        }
        await profileCtx.reloadProfile?.();
      } catch (error) {
        console.error('Error refreshing profile after default office update:', error);
      }
    }

    if (!hasFailure) {
      await executegetProfileData();
    }
    return !hasFailure;
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const saved = await saveUserProfileDetails(
        contactPhoneNumber,
        updatedOfficeDetail,
        updatedServiceOrderEmailNotification,
        bopsPreferences,
      );
      if (saved) {
        setHasUserEdited(false);
      }
      return saved;
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (resData && resData?.DefaultFirmLocationId) {
      setdefaultOfficeLocation(resData?.DefaultFirmLocationId);
    }
  }, [resData]);

  useEffect(() => {
    if (resData?.FirmAccess[0]?.Offices !== null) {
      resData?.FirmAccess[0]?.Offices.map((o: UserOffices) => {
        if (resData?.DefaultFirmLocationId == o.OfficeId) {
          setCurrentOfficeDetail(o);
          setUpdatedOffice(o);
        }
      });
    }
  }, [resData]);

  const setChangedOffice = (field: string, value: any) => {
    if (resData?.FirmAccess[0]?.Offices !== null) {
      resData?.FirmAccess[0]?.Offices.map((o: UserOffices) => {
        if (o.OfficeId == value) {
          setdefaultOfficeLocation(o.OfficeId);
          setUpdatedOffice(o);
        }
      });
    }
  };

  const setUpdatedEmail = (value: emailType[]) => {
    //remove empty emailaddress before saving Service Order Notification
    const filteredArray = value.filter((obj) => obj.ServiceOrderEmailAddress.trim() !== '');
    setupdatedServiceOrderEmailNotification(filteredArray);
  };

  const setField = (field: string, value: any) => {
    if (updatedOfficeDetail?.OfficeId != undefined) {
      setUpdatedOffice({
        ...updatedOfficeDetail,
        [field]: value,
      });
    }
  };

  useEffect(() => {
    if (resData?.ServiceOrderEmailNotification) {
      const searchOrderNotificationEmails: string[] = [];
      resData?.ServiceOrderEmailNotification?.forEach((item) => {
        const option = item.ServiceOrderEmailAddress;
        searchOrderNotificationEmails.push(option);
      });
    }
  }, []);

  useEffect(() => {
    if (resData?.BopsPreferences) {
      const bopsPreferencesUnsorted = resData.BopsPreferences.map((x, i) => {
        return { id: x.BopsPreferenceId, name: x.BopsPreferenceDescription };
      });
      const bopsPreferencesSorted = bopsPreferencesUnsorted.slice(1).sort((a, b) => a.name.localeCompare(b.name));
      const bopsPreferences = [bopsPreferencesUnsorted[0], ...bopsPreferencesSorted];
      setBopsPreferences(bopsPreferences);
      setinitialBopsPreferences(bopsPreferences);
    }
  }, [resData]);

  const [selectedUserAccessKey, setselectedUserAccessKey] = useState<userKeyPref[]>([]);
  const [vendorOptions, setVendorOptions] = useState<AccessKeyOptionType[]>([]);
  useEffect(() => {
    if (userAccessKeyData) {
      const selectedUserAccessKeyData = userAccessKeyData?.filter((x) => x.Status != 2);
      const selectedUserAccessKey: any = selectedUserAccessKeyData?.map((x, i) => {
        return {
          vendorName: x.ThirdPartyVendor.VendorName,
          ThirdPartyVendorId: x.ThirdPartyVendor.ThirdPartyVendorId,
        };
      });
      setselectedUserAccessKey(selectedUserAccessKey);
    }
  }, [resData]);

  useEffect(() => {
    const loadVendors = async () => {
      try {
        const token = await getAccessToken();
        const vendors = await getVendorOptions(token);
        setVendorOptions(vendors);
      } catch (error) {
        addSnackbarMessage({
          message: 'Failed to load access key options.',
          type: 'error',
        });
      }
    };
    loadVendors();
  }, []);

  const handleChangePasswordClick = () => {
    idaaSAuth.changePassword();
  };

  const onSelection = async (e: any) => {
    let reqBody;
    await e.map(async (k: AccessKeyOptionType) => {
      reqBody = {
        thirdPartyVendorId: k.ThirdPartyVendorId,
      };
    });
    const token = await getAccessToken();
    await AddUserAcessKeys(token, reqBody);
  };

  const handleBlur = () => {
    if (contactPhoneNumber.trim() === '') {
      setPhoneNumberError('Phone Number is required');
    }
  };

  const handleEmailDelete = (e: number) => {
    //delete email from the list;
  };

  const IsSOPreferenceVisible = () => {
    if (
      resData?.RoleTypeCdId === UserRoleTypes.Agent ||
      resData?.RoleId === UserRoles.OnBehalfOf ||
      resData?.RoleId === UserRoles.SystemAdministrator ||
      (resData?.RoleId === UserRoles.User &&
        profileCtx.profile?.activityRights?.find((o) => o.ActivityRightId === 71) &&
        profileCtx.profile?.activityRights?.find((o) => o.ActivityRightId === 73))
    ) {
      return true;
    }
    return false;
  };

  // The save bar should only appear once the user has actually changed something (AC #4).
  const normalizeEmails = (list?: { ServiceOrderEmailAddress?: string }[]) =>
    (list ?? [])
      .map((email) => email.ServiceOrderEmailAddress?.trim())
      .filter((email) => email)
      .sort();

  const isDirty = useMemo(() => {
    const phoneChanged =
      resData?.RoleTypeCdId !== UserRoleTypes.Agent &&
      contactPhoneNumber.trim() !== (resData?.PhoneNumber ?? '').trim();
    const officeChanged =
      updatedOfficeDetail?.OfficeId != undefined && !_.isEqual(updatedOfficeDetail, currentOfficeDetail);
    const emailsChanged = !_.isEqual(
      normalizeEmails(updatedServiceOrderEmailNotification),
      normalizeEmails(resData?.ServiceOrderEmailNotification),
    );
    const bopsChanged = !_.isEqual(bopsPreferences, initialBopsPreferences);
    return hasUserEdited && (phoneChanged || officeChanged || emailsChanged || bopsChanged);
  }, [
    resData,
    contactPhoneNumber,
    updatedOfficeDetail,
    currentOfficeDetail,
    updatedServiceOrderEmailNotification,
    bopsPreferences,
    initialBopsPreferences,
    hasUserEdited,
  ]);
  const navigationGuard = useUnsavedNavigationGuard({
    isDirty,
    onSave: handleSave,
  });

  return (
    <>
      <div className={classes.root}>
        <LoadingSpinner status={actionStatus} variant="linear" />

        {/*User Profile Banner */}
        {resData && (
          <AdminUserProfileBanner
            onChangeClick={handleChangePasswordClick}
            firstName={resData?.FirstName}
            middleName={resData?.MiddleName && resData?.MiddleName}
            lastName={resData?.LastName}
            userId={resData?.LoginUserId}
            roleId={resData?.RoleTypeCdId}
          />
        )}

        <div className={classes.contentWrap}>
          {/*Contact Information */}
          {resData && (
            <Grid container className={classes.fieldSection}>
              <Grid container spacing={2} sm={12} className={classes.fieldSectionRight}>
                <Grid item sm={3}>
                  <AgentNetTextInput
                    fullWidth
                    variant="outlined"
                    label={'Name'}
                    name="userProfileName"
                    data-testid="UserProfileName"
                    value={
                      ((resData?.FirstName && resData?.FirstName.trim()) ?? '') +
                      (resData?.FirstName && (resData?.MiddleName || resData?.LastName) ? ' ' : '') +
                      ((resData?.MiddleName && resData?.MiddleName.trim()) ?? '') +
                      (resData?.MiddleName && resData?.LastName ? ' ' : '') +
                      ((resData?.LastName && resData?.LastName.trim()) ?? '')
                    }
                    disabled
                    endAdornment={
                      resData?.RoleTypeCdId === UserRoleTypes.Agent ? (
                        <IconButton
                          className={classes.adornmentIcon}
                          disableFocusRipple
                          disableRipple
                          disableTouchRipple
                          data-testid="UserProfileNameEdit"
                          onClick={() => {
                            localStorage.setItem('isProfileEdited', 'true');
                            idaaSAuth.editProfile();
                          }}
                        >
                          <Edit htmlColor="#666" />
                        </IconButton>
                      ) : undefined
                    }
                  />
                </Grid>
                <Grid item sm={3}>
                  <AgentNetTextInput
                    fullWidth
                    variant="outlined"
                    label={'Email'}
                    name="userProfileEmail"
                    data-testid="UserProfileEmail"
                    value={resData?.EmailAddress}
                    disabled
                    endAdornment={
                      resData?.RoleTypeCdId === UserRoleTypes.Agent ? (
                        <IconButton
                          className={classes.adornmentIcon}
                          disableFocusRipple
                          disableRipple
                          disableTouchRipple
                          data-testid="UserProfileEmailEdit"
                          onClick={() => {
                            localStorage.setItem('isProfileEdited', 'true');
                            idaaSAuth.editEmail();
                          }}
                        >
                          <Edit htmlColor="#666" />
                        </IconButton>
                      ) : undefined
                    }
                  />
                </Grid>
                {resData?.RoleTypeCdId === UserRoleTypes.Agent ? (
                  <Grid item sm={3}>
                    <AgentNetTextInput
                      fullWidth
                      variant="outlined"
                      label={'Phone'}
                      name="userProfilePhone"
                      value={resData?.PhoneNumber}
                      disabled
                      data-testid="UserProfilePhoneEdit"
                      endAdornment={
                        <IconButton
                          className={classes.adornmentIcon}
                          disableFocusRipple
                          disableRipple
                          disableTouchRipple
                          onClick={() => {
                            localStorage.setItem('isProfileEdited', 'true');
                            idaaSAuth.editPhone();
                          }}
                        >
                          <Edit htmlColor="#666" />
                        </IconButton>
                      }
                    />
                  </Grid>
                ) : (
                  <Grid item sm={3}>
                    <AgentNetTextInput
                      fullWidth
                      variant="outlined"
                      label={'Phone'}
                      name="userProfilePhone"
                      value={contactPhoneNumber}
                      data-testid="UserProfilePhoneEdit"
                      onChange={(e: any) => {
                        setHasUserEdited(true);
                        setContactPhoneNumber(e.target.value);
                      }}
                      onBlur={handleBlur}
                    />
                    {contactPhoneNumber === '' ? <div style={{ color: 'red' }}>{phoneNumberError}</div> : null}
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}
          {/*Office Information */}
          <>
            {resData?.RoleTypeCdId === UserRoleTypes.Agent ? (
              <Grid container className={classes.fieldSection}>
                <Grid item sm={12} className={classes.fieldSectionLeft}>
                  <Typography variant="h2">Office Information</Typography>
                </Grid>
                <Grid container spacing={2} sm={12} className={classes.fieldSectionRight}>
                  <Grid item sm={6}>
                    <AgentNetDropdownSelector
                      name="userProfileOffice"
                      data-testid="UserProfileOffice"
                      label="Office"
                      dropdowntype="outlined"
                      options={resData?.FirmAccess[0]?.Offices.map((o: any) => {
                        return { name: o.Name, value: o.OfficeId };
                      })}
                      value={defaultOfficeLocation}
                      menuOption={(value: any, name?: any) => {
                        setHasUserEdited(true);
                        setChangedOffice('OfficeId', value);
                      }} //value={profileState?.officeId !== 0 ? offices?.find((office) => office.officeId === profileState?.officeId) : offices && offices[0]}
                    />
                  </Grid>
                  <Grid item sm={6}>
                    <AgentNetTextInput
                      fullWidth
                      variant="outlined"
                      label={'Address'}
                      name="userProfileOfficeAddress"
                      data-testid="UserProfileOfficeAddress"
                      value={`${updatedOfficeDetail.Address?.Address1} ${updatedOfficeDetail.Address?.Address2} ${updatedOfficeDetail.Address?.City} ${updatedOfficeDetail.Address?.State} ${updatedOfficeDetail.Address?.County} ${updatedOfficeDetail.Address?.PostalCode}`}
                      disabled
                    />
                  </Grid>
                  <Grid item sm={3}>
                    <PhoneField
                      fullWidth
                      variant="outlined"
                      label={'Phone'}
                      name="userProfileOfficePhone"
                      data-testid="UserProfileOfficePhone"
                      value={updatedOfficeDetail.PhoneNumber}
                      onChange={(e: any) => {
                        setHasUserEdited(true);
                        setField('PhoneNumber', e.target.value);
                      }}
                    />
                  </Grid>
                  <Grid item sm={3}>
                    <AgentNetTextInput
                      fullWidth
                      variant="outlined"
                      label={'FirmLicense'}
                      name="userProfileOfficeFirmLicense"
                      data-testid="UserProfileOfficeFirmLicense"
                      value={updatedOfficeDetail.FirmLicense}
                      onChange={(e: any) => {
                        setHasUserEdited(true);
                        setField('FirmLicense', e.target.value);
                      }}
                    />
                  </Grid>
                  <Grid item sm={3}>
                    <AgentNetTextInput
                      fullWidth
                      variant="outlined"
                      label={'ALTAID'}
                      name="userProfileOfficeALTAID"
                      data-testid="UserProfileOfficeALTAID"
                      value={updatedOfficeDetail.AltaId}
                      disabled
                    />
                  </Grid>
                </Grid>
              </Grid>
            ) : null}
          </>

          {/*Search Order Email Notifications Component */}
          {/*Pass the email list down to this component */}
          <>
            {resData?.RoleTypeCdId === UserRoleTypes.Agent ? (
              <Grid container className={classes.fieldSection}>
                <Grid item sm={12} className={classes.sectionHeaderRow}>
                  <Typography variant="h2">Service Order Email Notifications</Typography>
                  <AgentNetButton
                    variant="contained"
                    color="primary"
                    size="small"
                    data-testid="UserSoEmail"
                    onClick={() => emailTableRef.current?.addEmail()}
                    disabled={isSaveDisabled}
                    plusIcon
                  >
                    Add Email
                  </AgentNetButton>
                </Grid>
                <Grid container spacing={2} sm={12} className={classes.fieldSectionRight}>
                  <SearchOrderEmailsTable
                    ref={emailTableRef}
                    emails={resData?.ServiceOrderEmailNotification}
                    onDelete={(e) => handleEmailDelete(e)}
                    onChange={(value: any) => {
                      setHasUserEdited(true);
                      setUpdatedEmail(value);
                    }}
                    isDuplicate={(value: any) => {
                      if (value == true) {
                        setisSaveDisabled(true);
                      } else {
                        setisSaveDisabled(false);
                      }
                    }}
                    onValidate={(value: any) => {
                      if (value == false) {
                        setisSaveDisabled(true);
                      } else {
                        setisSaveDisabled(false);
                      }
                    }}
                    qaBtnprefix="UserSoEmail"
                  />
                </Grid>
              </Grid>
            ) : null}
          </>

          {/*Title Commitment Download Preferences Component */}
          {/*Pass the list of options down to this component */}
          <>
            {IsSOPreferenceVisible() && (
              <Grid container className={classes.fieldSection}>
                <Grid item sm={12} className={classes.fieldSectionLeft}>
                  <Typography variant="h2">Title Commitment Download Preferences</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Select the preferred Download option based on your title software
                  </Typography>
                </Grid>
                <Grid container sm={12} className={classes.preferenceContent}>
                  <BopsPreferencesChecklist
                    options={getFilteredSortedBopsOptions(isCoverSheetEnabled)}
                    value={bopsPreferences}
                    onChange={(value: BopsPreferences[]): void => {
                      const sortedValue: BopsPreferences[] = [
                        value[0],
                        ...value
                          .slice(1)
                          .sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name)),
                      ];
                      setHasUserEdited(true);
                      setBopsPreferences(sortedValue);
                    }}
                    qaBtnprefix="PreferredDownloads"
                  />
                </Grid>
              </Grid>
            )}
          </>

          {/*User Access Keys Component */}
          {/*Pass the list of options down to this component */}

          {resData?.RoleTypeCdId === UserRoleTypes.Agent ? (
            <Grid container className={classes.fieldSection}>
              <Grid item sm={12} className={classes.sectionHeaderRow}>
                <div>
                  <Typography variant="h2">User Access Keys</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Create a User Access Key to use in place of your username and password when accessing AgentNet from
                    a vendor application.
                  </Typography>
                </div>
                <AgentNetButton
                  variant="contained"
                  color="primary"
                  size="small"
                  data-testid="AddUserAccessKeys"
                  onClick={(e) => userAccessKeyRef.current?.togglePopper(e.currentTarget as HTMLElement)}
                  plusIcon
                >
                  Add Access Key
                </AgentNetButton>
              </Grid>
              <Grid container spacing={2} sm={12} className={classes.fieldSectionRight}>
                <UserAccessKeyComponent
                  ref={userAccessKeyRef}
                  onChange={(e) => onSelection(e)}
                  accessKeyOptions={vendorOptions}
                  value={selectedUserAccessKey}
                />
              </Grid>
            </Grid>
          ) : null}
        </div>
        {resData && isDirty && (
          <BottomAppBar
            primaryActionLabel="Save Changes"
            cancelDataQa={'UserProfileCancel'}
            cancelLabel="Cancel"
            primaryActionDataQa={'UserProfileSave'}
            onPrimaryClick={handleSave}
            primaryButtonLoader={isSaving}
            disablePrimary={isSaveDisabled || isSaving}
            onCancel={() => window.location.reload()}
          />
        )}
      </div>
      <UnsavedChangesDialog
        open={navigationGuard.isOpen}
        saving={navigationGuard.isSaving}
        onSave={navigationGuard.saveAndProceed}
        onDiscard={navigationGuard.discardAndProceed}
        onStay={navigationGuard.stay}
      />
    </>
  );
};

export default AdminUserProfileContent;
